import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Bell, Newspaper, Calendar, ArrowRight, ExternalLink } from 'lucide-react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useNews } from '../contexts/NewsContext'
import { useAnnouncement } from '../contexts/AnnouncementContext'

const AnnouncementNews = () => {
  const { t } = useTranslation()
  const { newsItems, fetchNewsItems } = useNews()
  const { announcements, fetchAnnouncements } = useAnnouncement()

  useEffect(() => {
    fetchNewsItems({ isActive: true, limit: 2 })
    fetchAnnouncements({ isActive: true, limit: 3 })
  }, [])

  // Use first 3 announcements and first 2 news items for homepage
  const displayAnnouncements = announcements.slice(0, 3)
  const displayNews = newsItems.slice(0, 2)

  return (
    <section className="py-16 section-light">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Announcements Section */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-primary-dark to-primary-mid text-white p-6">
                <div className="flex items-center space-x-3">
                  <Bell className="w-8 h-8" />
                  <h2 className="text-3xl font-bold">ANNOUNCEMENT</h2>
                </div>
              </div>

              {/* Announcements List */}
              <div className="p-6">
                <div className="space-y-3">
                  {displayAnnouncements.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No announcements available</p>
                  ) : (
                    displayAnnouncements.map((announcement, index) => (
                      <motion.div
                        key={announcement._id}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: index * 0.1 }}
                        viewport={{ once: true }}
                        className={`p-4 rounded-lg border-l-4 transition-all duration-300 hover:shadow-lg cursor-pointer ${
                          announcement.important
                            ? 'border-red-500 bg-red-50 hover:bg-red-100'
                            : 'border-primary-mid bg-primary-light/30 hover:bg-primary-light/50'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <span className={`px-2 py-1 text-xs font-semibold rounded ${
                                announcement.type === 'Exam' 
                                  ? 'bg-accent-red/10 text-accent-red' 
                                  : 'bg-primary-mid/10 text-primary-mid'
                              }`}>
                                {announcement.type}
                              </span>
                              {announcement.important && (
                                <span className="px-2 py-1 text-xs font-semibold rounded bg-yellow-100 text-yellow-700 flex items-center">
                                  <Bell className="w-3 h-3 mr-1" />
                                  Important
                                </span>
                              )}
                            </div>
                            <h3 className="font-semibold text-gray-800 mb-2 line-clamp-2 text-sm">
                              {announcement.title}
                            </h3>
                            <div className="flex items-center text-xs text-gray-600">
                              <Calendar className="w-3.5 h-3.5 mr-1.5" />
                              {new Date(announcement.date).toLocaleDateString('en-US', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric'
                              })}
                            </div>
                          </div>
                          <ArrowRight className="w-4 h-4 text-primary-dark flex-shrink-0 ml-2" />
                        </div>
                      </motion.div>
                    ))
                  )}
                </div>

                {/* View More Button */}
                <div className="mt-6 text-center">
                  <Link
                    to="/news"
                    className="inline-flex items-center space-x-2 px-6 py-2.5 bg-primary-dark text-white rounded-lg hover:bg-primary-mid transition-all duration-300 font-semibold text-sm shadow-lg hover:shadow-xl"
                  >
                    <span>View More</span>
                    <ExternalLink className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>

          {/* News & Updates Section */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-primary-dark to-primary-mid text-white p-6">
                <div className="flex items-center space-x-3">
                  <Newspaper className="w-8 h-8" />
                  <div>
                    <h2 className="text-3xl font-bold">NEWS & UPDATES</h2>
                  </div>
                </div>
              </div>

              {/* News Cards */}
              <div className="p-6 space-y-4">
                {displayNews.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No news available</p>
                ) : (
                  displayNews.map((news, index) => (
                    <motion.div
                      key={news._id}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      viewport={{ once: true }}
                      className="group cursor-pointer"
                    >
                      <div className="bg-gradient-to-br from-primary-light/20 to-primary-mid/10 rounded-xl overflow-hidden hover:shadow-xl transition-all duration-300">
                        {/* Image */}
                        <div className="relative h-40 overflow-hidden">
                          {news.image?.url ? (
                            <img
                              src={news.image.url}
                              alt={news.title}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-primary-dark to-primary-mid flex items-center justify-center">
                              <Newspaper className="w-16 h-16 text-white opacity-50" />
                            </div>
                          )}
                          <div className="absolute top-3 left-3">
                            <span className="px-2.5 py-1 bg-white text-primary-dark rounded-full text-xs font-semibold">
                              {news.category}
                            </span>
                          </div>
                        </div>

                        {/* Content */}
                        <div className="p-4">
                          <h3 className="text-lg font-bold text-gray-800 mb-2 group-hover:text-primary-dark transition-colors duration-300 line-clamp-2">
                            {news.title}
                          </h3>
                          <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                            {news.description}
                          </p>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center text-xs text-gray-500">
                              <Calendar className="w-3.5 h-3.5 mr-1.5" />
                              {new Date(news.publishDate).toLocaleDateString('en-IN', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric'
                              })}
                            </div>
                            <button className="flex items-center space-x-1 text-primary-mid font-semibold text-xs hover:text-primary-dark transition-colors duration-300">
                              <span>Read More</span>
                              <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform duration-300" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))
                )}
              </div>

              {/* View More Button */}
              <div className="px-6 pb-6 text-center">
                <Link
                  to="/news"
                  className="inline-flex items-center space-x-2 px-6 py-2.5 bg-white border-2 border-primary-mid text-primary-mid rounded-lg hover:bg-primary-mid hover:text-white transition-all duration-300 font-semibold text-sm"
                >
                  <span>Read More</span>
                  <ExternalLink className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

export default AnnouncementNews
